import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sword, Coins, Star, Calendar, Crown, CheckCircle2, Plus } from 'lucide-react';
import { codexApi } from '../api';
import { celebrate } from '../../lib/celebrate';

function ProgressBar({ value, target, accent = 'var(--codex-gold)' }) {
  const pct = Math.min(100, Math.round((value / Math.max(1, target)) * 100));
  return (
    <div style={{
      height: 8,
      borderRadius: 999,
      background: 'rgba(0,0,0,0.4)',
      overflow: 'hidden',
      border: '1px solid rgba(245,217,124,0.15)',
    }}>
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.9, ease: 'easeOut' }}
        style={{
          height: '100%',
          background: `linear-gradient(90deg, ${accent} 0%, var(--codex-orange) 100%)`,
          boxShadow: `0 0 10px ${accent}66`,
        }}
      />
    </div>
  );
}

function ChallengeCard({ ch, onTick }) {
  const isMentor = ch.period === 'mentor';
  const isWeekly = ch.period === 'weekly';
  const completed = !!ch.completed;
  const accent = completed ? 'var(--codex-gold)' : (isMentor ? '#a855f7' : isWeekly ? '#7dd3fc' : 'var(--codex-gold)');
  const PeriodIcon = isMentor ? Crown : isWeekly ? Calendar : Star;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      data-testid={`mission-${ch.slug || ch.id}`}
      className="codex-card"
      style={{
        padding: 18,
        borderColor: completed ? 'rgba(212,175,55,0.4)' : 'rgba(245,217,124,0.15)',
        boxShadow: completed ? '0 0 24px rgba(212,175,55,0.18)' : 'none',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {completed && (
        <div style={{
          position: 'absolute', top: 14, right: 14,
          color: 'var(--codex-gold-light)', display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 11, letterSpacing: '0.2em', fontFamily: "'Cinzel', serif",
        }}>
          <CheckCircle2 size={14} /> COMPLETE
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: accent }}>
        <PeriodIcon size={14} />
        <span className="codex-eyebrow" style={{ color: 'inherit' }}>
          {isMentor ? 'MENTOR MISSION' : isWeekly ? 'WEEKLY MISSION' : 'DAILY MISSION'}
        </span>
      </div>
      <div className="codex-display" style={{ fontSize: 18, color: 'var(--codex-gold-light)', marginBottom: 6 }}>
        {ch.title}
      </div>
      <div style={{ fontSize: 13, color: 'rgba(253,246,227,0.78)', marginBottom: 14, lineHeight: 1.5 }}>
        {ch.description}
      </div>
      <ProgressBar value={ch.progress || 0} target={ch.target || 1} accent={accent} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: 12, color: 'rgba(253,246,227,0.7)' }}>
        <span>{ch.progress || 0} / {ch.target || 1}</span>
        <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {ch.reward_shekels > 0 && (
            <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', color: 'var(--codex-gold)' }}>
              <Coins size={12} /> +{ch.reward_shekels}
            </span>
          )}
          {ch.reward_xp > 0 && (
            <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', color: 'var(--codex-purple-light)' }}>
              <Star size={12} /> +{ch.reward_xp} XP
            </span>
          )}
        </span>
      </div>

      {ch.goal_kind === 'manual' && !completed && (
        <button
          type="button"
          onClick={() => onTick(ch)}
          data-testid={`mission-tick-${ch.id}`}
          className="codex-btn codex-btn-primary"
          style={{ marginTop: 12, width: '100%', padding: '8px 12px', fontSize: 12 }}
        >
          <Plus size={13} /> Mark progress
        </button>
      )}
    </motion.div>
  );
}

export default function CodexMissions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchAll = async () => {
    try {
      const d = await codexApi.challengesMe();
      setData(d);
      if (d.rewards_paid_now?.shekels > 0) {
        celebrate(0.6);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const tick = async (ch) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await codexApi.challengesManualProgress(ch.id);
      if (res.rewards_paid_now?.shekels > 0) celebrate(0.6);
      await fetchAll();
    } finally { setBusy(false); }
  };

  const counts = useMemo(() => {
    if (!data) return { d: 0, w: 0, m: 0, dc: 0, wc: 0, mc: 0 };
    return {
      d: data.daily.length,
      w: data.weekly.length,
      m: data.mentor_assigned.length,
      dc: data.daily.filter((c) => c.completed).length,
      wc: data.weekly.filter((c) => c.completed).length,
      mc: data.mentor_assigned.filter((c) => c.completed).length,
    };
  }, [data]);

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--codex-gold-light)' }} data-testid="missions-loading">
        Calling forth your missions...
      </div>
    );
  }
  if (!data) return null;

  return (
    <div data-testid="codex-missions-page">
      <div style={{ marginBottom: 20 }}>
        <div className="codex-eyebrow">SACRED MISSIONS</div>
        <h1 className="codex-h1" style={{ marginBottom: 8 }}>
          <Sword size={28} style={{ verticalAlign: '-4px', marginRight: 10, color: 'var(--codex-gold)' }} />
          Today's Charge
        </h1>
        <p style={{ color: 'rgba(253,246,227,0.78)', maxWidth: 640 }}>
          A faithful walk needs daily rhythm and weekly weight. Complete your missions to earn Shekels and grow.
        </p>
      </div>

      {/* Stats strip */}
      <div className="codex-card" style={{ padding: 18, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14 }} data-testid="missions-stats">
        <div>
          <div className="codex-eyebrow" style={{ color: 'var(--codex-gold-light)' }}><Star size={11} style={{ verticalAlign: '-1px' }} /> DAILY</div>
          <div className="codex-display" style={{ fontSize: 22, color: 'var(--codex-parchment)' }}>{counts.dc} / {counts.d}</div>
        </div>
        <div>
          <div className="codex-eyebrow" style={{ color: '#7dd3fc' }}><Calendar size={11} style={{ verticalAlign: '-1px' }} /> WEEKLY</div>
          <div className="codex-display" style={{ fontSize: 22, color: 'var(--codex-parchment)' }}>{counts.wc} / {counts.w}</div>
        </div>
        <div>
          <div className="codex-eyebrow" style={{ color: '#c4b5fd' }}><Crown size={11} style={{ verticalAlign: '-1px' }} /> MENTOR</div>
          <div className="codex-display" style={{ fontSize: 22, color: 'var(--codex-parchment)' }}>{counts.mc} / {counts.m}</div>
        </div>
      </div>

      <AnimatePresence>
        {data.rewards_paid_now?.shekels > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            data-testid="missions-rewards-banner"
            style={{
              padding: '12px 16px',
              borderRadius: 12,
              background: 'linear-gradient(90deg, rgba(212,175,55,0.18), rgba(234,88,12,0.18))',
              border: '1px solid rgba(212,175,55,0.4)',
              color: 'var(--codex-gold-light)',
              marginBottom: 20,
              fontSize: 13,
            }}
          >
            <Coins size={15} style={{ verticalAlign: '-3px', marginRight: 8 }} />
            +{data.rewards_paid_now.shekels} Shekels and +{data.rewards_paid_now.xp} XP for completed missions.
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mentor missions first */}
      {data.mentor_assigned.length > 0 && (
        <section style={{ marginBottom: 30 }} data-testid="missions-mentor-section">
          <h2 className="codex-h2" style={{ color: 'var(--codex-gold-light)', marginBottom: 12 }}>
            <Crown size={18} style={{ verticalAlign: '-3px', marginRight: 8 }} />
            From Your Mentor
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {data.mentor_assigned.map((ch) => <ChallengeCard key={ch.id} ch={ch} onTick={tick} />)}
          </div>
        </section>
      )}

      {/* Daily */}
      <section style={{ marginBottom: 30 }} data-testid="missions-daily-section">
        <h2 className="codex-h2" style={{ color: 'var(--codex-gold-light)', marginBottom: 12 }}>
          <Star size={18} style={{ verticalAlign: '-3px', marginRight: 8 }} />
          Daily Missions
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {data.daily.map((ch) => <ChallengeCard key={ch.id} ch={ch} onTick={tick} />)}
        </div>
      </section>

      {/* Weekly */}
      <section data-testid="missions-weekly-section">
        <h2 className="codex-h2" style={{ color: 'var(--codex-gold-light)', marginBottom: 12 }}>
          <Calendar size={18} style={{ verticalAlign: '-3px', marginRight: 8 }} />
          Weekly Mission
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {data.weekly.map((ch) => <ChallengeCard key={ch.id} ch={ch} onTick={tick} />)}
        </div>
      </section>
    </div>
  );
}
